import { Proto } from "../../../pojo/Proto";

export namespace Workflow {
    export interface WorkflowProcess {
        workflowProcessId?: number;
        workflowProcessName: string;
        description: string;
        enabled: boolean;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        stages?: WorkflowStage[];
        stageTransitions?: WorkflowStageTransition[];
    }

    export interface WorkflowStage {
        workflowStageId?: number;
        workflowProcessId?: number;
        stageName: string;
        isTerminal?: boolean;
        isStartStage?: boolean; // True for the start stage (only one per process)
        description: string;
        defaultEnabled?: boolean;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
        states?: WorkflowState[]; // States belonging to this stage
        actions?: WorkflowAction[]; // Actions for terminal stages
    }

    export interface WorkflowState {
        workflowStateId?: number;
        workflowProcessId?: number;
        workflowStageId?: number; // Reference to parent stage
        stateName: string;
        description: string;
        defaultEnabled?: boolean;
        approvalGroupId?: number;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
        contacts?: WorkflowStateContact[]; // Frontend-only contacts array
    }

    export interface WorkflowStateContact {
        workflowStateId?: number;
        contId: number;
        contactName?: string; // Frontend-only contact name
    }

    export interface WorkflowStageTransition {
        workflowStageTransitionId?: number;
        workflowProcessId?: number;
        fromStageId?: number;
        toStageId?: number;
        fromStageName: string; // Used for frontend stage mapping
        toStageName: string; // Used for frontend stage mapping
        transitionLabel: string;
        transitionType: "APPROVE" | "DENY" | "REQUEST_CHANGES";
        description: string;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
    }

    export interface WorkflowAction {
        workflowActionId?: number;
        workflowProcessId?: number;
        workflowStageId?: number; // Now associated with stages, not states
        actionItemTypeId?: number; // Required for field context
        actionItemId?: number; // Required for field context
        actionType: "HTTP_REQUEST" | "SYSTEM_ACTION";
        actionCode?: "SEND_EMAIL" | "CREATE_EVENT"; // For system actions - matches ActionCode enum
        isSystemAction?: boolean; // True for system-defined actions
        sortOrder?: number;
        httpUrl?: string;
        httpMethod?: string;
        httpHeaders?: string;
        jsonPayloadTemplate?: string;
        description: string;
        defaultEnabled?: boolean; // Default enabled status for workflow action
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
        inputs?: WorkflowActionInput[]; // System action inputs
    }

    export interface WorkflowActionInput {
        workflowActionInputId?: number;
        workflowActionId?: number;
        inputCode: string; // e.g., 'subject', 'body', 'to', 'from'
        inputValue: string; // The actual input value
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
    }

    export interface WorkflowActionHistory {
        workflowActionHistoryId?: number;
        instance?: string;
        workflowProcessInstanceId?: number;
        originalWorkflowStageId?: number;
        originalWorkflowActionId?: number;
        workflowItemTypeId?: number;
        workflowItemId?: number;
        workflowItemName?: string;
        actionType?: string;
        actionCode?: string;
        status?: "RUNNING" | "COMPLETED" | "ERROR";
        errorMessage?: string;
        errorStackTrace?: string;
        httpResponseCode?: number;
        httpResponseBody?: string;
        executionDate?: Proto.ISODateString;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
    }

    export type WorkflowStatus = "PENDING" | "IN_PROGRESS" | "COMPLETED" | "DENIED" | "REQUESTED_CHANGES";

    export const WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE = 10_001;
    export const BUDGET_PLAN_OBJECT_TYPE = 12_001;
    export const WORKFLOW_PROCESS_TYPE_ID = 9001;

    export interface WorkflowSubmissionEntry {
        itemTypeId?: string;
        itemId?: string;
        itemName?: string;
        itemValue?: string;
        itemDataType?: string;
        extraData?: { [key: string]: string };
    }

    export interface WorkflowItem {
        parentItemTypeId?: number;
        parentItemId?: number;
        itemTypeId?: number;
        itemId?: number;
        name?: string;
        workflowSubmissionList?: WorkflowSubmissionEntry[];
        workflowProcessId?: number;
        currentWorkflowStageId?: number;
        currentWorkflowStage?: WorkflowStage; // only stageName is populated
        contId?: number;
        todoId?: number;
        workflowStatus?: WorkflowStatus;
        lastModUser?: string;
        lastModDate?: Proto.ISODateString;
        createDate?: Proto.ISODateString;
        submissionDate?: Proto.ISODateString;
        submitted?: boolean;

        // frontend-only fields
        itemTypeName?: string;
        submitterName?: string;
    }

    export type SystemAction = {
        actionCode: string;
        displayName: string;
        description: string;
        inputs?: SystemActionInput[];
    };

    export type SystemActionInput = {
        inputCode: string;
        label: string;
        type: "text" | "textarea";
        required: boolean;
        help?: string;
    };
}
